import Axios from "../utils/axiosInterceptor";
import { HMO_QUERY_APIS } from "../utils/constants";
import { StatusCodes } from "http-status-codes";
import { IPatientFilter } from "../interfaces/IPatient";
import { errorMessage } from "../utils/notificationUtils";
export type HmoQueryKind =
  | "STAGE_NMI"
  | "DOCUMENT_REQUEST"
  | "DOCUMENT_DEFICIENCY";

export type HmoQueryResponse = {
  id: number;
  caseId?: number;
  visitId?: number;
  queryKind?: HmoQueryKind;
  receivedQuery?: string;
  queryQuestion?: string;
  updatedRemarks?: string;
  queryStage?: string;
  stageId?: number;
  typeOfQuery?: string;
  queryType?: string;
  status?: boolean;
  stateName?: string;
  returnedResponse?: string;
  returnedDocumentUrl?: string;
  returnedDocuments?: {
    id?: number;
    documentTypeName?: string;
    documentName?: string;
    documentUrl?: string;
    viewUrl?: string;
    documentLabelType?: string;
  }[];
  queryRequiredDocuments?: { id?: number; name: string; expiryDate?: string; remarks?: string }[];
  oldDocumentId?: number;
  newDocumentId?: number;
  deficiencyQuery?: boolean;
  casePreAuthId?: number;
  createdAt?: string;
  updatedAt?: string;
  responseDateTime?: string;
  raisedByRole?: string;
};

export type CreateStageNmiRequest = {
  queryStage: string;
  stageId?: number;
  remarks?: string;
  casePreAuthId?: number;
  requiredDocuments?: { id?: number; name: string; expiryDate?: string; remarks?: string }[];
};

export type CreateManualQueryRequest = {
  queryStage: string;
  stageId?: number;
  queryQuestion: string;
  typeOfQuery?: string;
  assignToFrontOffice?: boolean;
  requiredDocuments?: { id?: number; name: string; expiryDate?: string; remarks?: string }[];
  casePreAuthId?: number;
};

export type QueryUploadedDocumentResponse = {
  id: number;
  documentTypeName?: string;
  documentName?: string;
  documentUrl?: string;
  viewUrl?: string;
  documentLabelType?: string;
};

export type QueryReplyRequest = {
  returnedResponse?: string;
  oldDocumentId?: number;
  newDocumentId?: number;
  returnedDocumentIds?: number[];
};

export type QueryReplyOptions = {
  returnedResponse?: string;
  newDocumentId?: number;
  oldDocumentId?: number;
  files?: { file: File; documentType?: string }[];
};

export const getHmoQuery = async (
  visitId: number | string,
  queryId: number | string,
): Promise<HmoQueryResponse> => {
  const response = await Axios.get(
    `${HMO_QUERY_APIS.LIST(visitId)}/${queryId}`,
  );
  if (response.status === StatusCodes.OK) {
    return response.data;
  }
  throw new Error("Unable to fetch query");
};

export type DocumentReviewRequest = {
  action: "APPROVE" | "REJECT";
  remark?: string;
  queryStage?: string;
  stageId?: number;
};

export type DocumentReviewResponse = {
  documentId: number;
  deficiencyCheckStatus?: boolean | null;
  remarks?: string;
  query?: HmoQueryResponse;
};

export const listHmoQueries = async (
  visitId: number | string,
  params?: { stageId?: number; openOnly?: boolean },
): Promise<HmoQueryResponse[]> => {
  const response = await Axios.get(HMO_QUERY_APIS.LIST(visitId), { params });
  if (response.status === StatusCodes.OK) {
    return response.data;
  }
  throw new Error("Unable to fetch queries");
};

export const createStageNmi = async (
  visitId: number | string,
  body: CreateStageNmiRequest,
): Promise<HmoQueryResponse> => {
  const response = await Axios.post(HMO_QUERY_APIS.STAGE_NMI(visitId), body);
  if (response.status === StatusCodes.OK) {
    return response.data;
  }
  throw new Error("Unable to create stage NMI");
};

export const createManualQuery = async (
  visitId: number | string,
  body: CreateManualQueryRequest,
): Promise<HmoQueryResponse> => {
  const response = await Axios.post(HMO_QUERY_APIS.CREATE(visitId), body);
  if (response.status === StatusCodes.OK) {
    return response.data;
  }
  throw new Error("Unable to create query");
};

export const fetchQueryDocumentViewUrl = async (
  documentId: number | string,
): Promise<QueryUploadedDocumentResponse> => {
  const response = await Axios.get(HMO_QUERY_APIS.DOCUMENT_VIEW_URL(documentId), {
    skipSessionRedirect: true,
  });
  if (response.status === StatusCodes.OK) {
    return response.data;
  }
  throw new Error("Unable to load query document");
};

export const fetchQueryDocumentPreviewBlob = async (
  documentId: number | string,
): Promise<{ blobUrl: string; contentType: string }> => {
  const response = await Axios.get(HMO_QUERY_APIS.DOCUMENT_DOWNLOAD(documentId, true), {
    responseType: "blob",
    skipSessionRedirect: true,
  });
  if (response.status !== StatusCodes.OK) {
    throw new Error("Unable to load query document");
  }
  const contentType =
    response.headers?.["content-type"] ||
    response.headers?.["Content-Type"] ||
    "application/octet-stream";
  const blobUrl = window.URL.createObjectURL(
    new Blob([response.data], { type: contentType }),
  );
  return { blobUrl, contentType };
};

export const downloadQueryDocument = async (
  documentId: number | string,
  fallbackName?: string,
): Promise<void> => {
  const response = await Axios.get(
    HMO_QUERY_APIS.DOCUMENT_DOWNLOAD(documentId, false),
    { responseType: "blob", skipSessionRedirect: true },
  );
  if (response.status !== StatusCodes.OK) {
    throw new Error("Unable to download document");
  }

  const disposition: string =
    response.headers?.["content-disposition"] ||
    response.headers?.["Content-Disposition"] ||
    "";
  const match = /filename\*?=(?:UTF-8'')?"?([^";]+)"?/i.exec(disposition);
  const fileName = match?.[1]
    ? decodeURIComponent(match[1])
    : fallbackName || `document-${documentId}`;

  const contentType =
    response.headers?.["content-type"] ||
    response.headers?.["Content-Type"] ||
    "application/octet-stream";

  const blobUrl = window.URL.createObjectURL(
    new Blob([response.data], { type: contentType }),
  );
  const anchor = document.createElement("a");
  anchor.href = blobUrl;
  anchor.download = fileName;
  document.body.appendChild(anchor);
  anchor.click();
  anchor.remove();
  window.URL.revokeObjectURL(blobUrl);
};

export const replyToHmoQuery = async (
  visitId: number | string,
  queryId: number | string,
  options: QueryReplyOptions | QueryReplyRequest,
): Promise<HmoQueryResponse> => {
  const files =
    "files" in options
      ? options.files?.filter((item) => item?.file)
      : undefined;
  const returnedResponse = options.returnedResponse;
  const newDocumentId = options.newDocumentId;
  const oldDocumentId =
    "oldDocumentId" in options ? options.oldDocumentId : undefined;
  const returnedDocumentIds =
    "returnedDocumentIds" in options ? options.returnedDocumentIds : undefined;
  const hasUploadFiles = Boolean(files && files.length > 0);

  if (hasUploadFiles) {
    const formData = new FormData();
    if (returnedResponse) {
      formData.append("returnedResponse", returnedResponse);
    }
    if (newDocumentId != null) {
      formData.append("newDocumentId", String(newDocumentId));
    }
    if (oldDocumentId != null) {
      formData.append("oldDocumentId", String(oldDocumentId));
    }
    for (const item of files || []) {
      formData.append("files", item.file);
      formData.append(
        "documentTypes",
        item.documentType || "OTHER",
      );
    }
    const response = await Axios.post(
      HMO_QUERY_APIS.REPLY(visitId, queryId),
      formData,
      { skipSessionRedirect: true },
    );
    if (response.status === StatusCodes.OK) {
      return response.data;
    }
    throw new Error("Unable to reply to query");
  }

  const response = await Axios.post(HMO_QUERY_APIS.REPLY(visitId, queryId), {
    returnedResponse,
    newDocumentId,
    oldDocumentId,
    returnedDocumentIds,
  });
  if (response.status === StatusCodes.OK) {
    return response.data;
  }
  throw new Error("Unable to reply to query");
};

export const reviewDocument = async (
  documentId: number | string,
  body: DocumentReviewRequest,
): Promise<DocumentReviewResponse> => {
  const response = await Axios.post(HMO_QUERY_APIS.DOCUMENT_REVIEW(documentId), body);
  if (response.status === StatusCodes.OK) {
    return response.data;
  }
  throw new Error("Unable to review document");
};

export const applyHmoQueryAction = async (
  visitId: number | string,
  queryId: number | string,
  action: "ACCEPT" | "CLOSE" | "REQUERY",
  remarks?: string,
): Promise<HmoQueryResponse> => {
  const response = await Axios.post(HMO_QUERY_APIS.ACTION(visitId, queryId), {
    action,
    remarks,
  });
  if (response.status === StatusCodes.OK) {
    return response.data;
  }
  throw new Error("Unable to apply query action");
};

const mapQuerySubStatusForApi = (value?: string | null): string | undefined => {
  if (!value || value === "ALL") return undefined;
  if (value.toUpperCase() === "REPLIED") return "RESPONDED";
  return value;
};

const buildQueryListHeaders = (
  filters: IPatientFilter,
  healthCareId: number,
  pageNumber?: number,
  pageSize?: number,
): Record<string, string | undefined> => ({
  "Content-Type": "application/json",
  endDate: filters.endDate || undefined,
  search: filters.searchTerm || "",
  startDate: filters.startDate || undefined,
  healthCareId: String(healthCareId),
  all: Number(healthCareId) <= 0 ? "yes" : "no",
  pageNumber:
    pageNumber != null ? String(pageNumber) : String((filters.pageNumber || 1) - 1),
  pageSize: String(pageSize ?? filters.pageSize ?? 10),
  referralSubStatus: mapQuerySubStatusForApi(filters.referralSubStatus),
  querySubStatus: mapQuerySubStatusForApi(filters.referralSubStatus),
  workflowVisitType: filters.workflowVisitType || undefined,
  departmentId:
    filters?.departmentId != null && Number(filters.departmentId) > 0
      ? String(filters.departmentId)
      : undefined,
});

export const fetchQueryListPL = async (
  filters: IPatientFilter,
  healthCareId: number,
) => {
  try {
    const response = await Axios.get(HMO_QUERY_APIS.LIST_PL, {
      headers: buildQueryListHeaders(filters, healthCareId),
    });
    if (response.status === StatusCodes.OK) {
      return {
        patientResponseList: response.data.patientCaseVisitDTOList,
        tabAlertSummary: response.data.tabAlertSummary,
        referralSubStatusCounts:
          response.data.referralSubStatusCounts ||
          response.data.querySubStatusCounts,
        workflowVisitTypeTabCounts: response.data.workflowVisitTypeTabCounts,
        totalElements: response.data.totalElements,
        totalPages: response.data.totalPages,
        status: response.status,
      };
    }
    return {
      errorMessage: "Cannot fetch query list",
      status: response.status,
    };
  } catch (error) {
    errorMessage("Something went wrong. Please, Try again later");
    console.error("Error fetching query list:", error);
    throw new Error("Cannot fetch query list");
  }
};

export const fetchQueryCountPL = async (
  filters: IPatientFilter,
  healthCareId: number,
): Promise<number> => {
  const counts = await fetchQueryTabCountsPL(filters, healthCareId);
  return counts.openCount;
};

export const fetchQueryTabCountsPL = async (
  filters: IPatientFilter,
  healthCareId: number,
): Promise<{ openCount: number; subStatusCounts: Record<string, number> }> => {
  const response = await Axios.get(HMO_QUERY_APIS.COUNT, {
    headers: {
      ...buildQueryListHeaders(filters, healthCareId),
      referralSubStatus: undefined,
      querySubStatus: undefined,
    },
  });
  if (response.status === StatusCodes.OK) {
    const subStatusCounts =
      response.data?.referralSubStatusCounts ||
      response.data?.querySubStatusCounts ||
      {};
    return {
      openCount: Number(response.data?.count ?? 0),
      subStatusCounts,
    };
  }
  return { openCount: 0, subStatusCounts: {} };
};
